import React, { useEffect, useState } from 'react';
import { GAME_SCREENS, BUTTON_TEXT, ANIMATIONS, Z_INDEX, ASSET_URLS } from '../constants';
import type { GameScreen } from '../constants';
import { useGameAudio } from '../GameAudioProvider';

interface BackButtonProps {
  currentScreen: GameScreen;
  currentLevel: number;
  onBack: (newScreen: GameScreen, newLevel: number) => void;
  className?: string;
  style?: React.CSSProperties;
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'custom';
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'minimal' | 'floating';
}

declare global {
  interface Window {
    flutterExit?: () => void;
  }
}

const BackButton: React.FC<BackButtonProps> = ({
  currentScreen,
  currentLevel,
  onBack,
  className = '',
  style,
  position = 'top-left',
  size = 'md',
  variant = 'default'
}) => {
  const [isPressed, setIsPressed] = useState(false);
  const { stopAudio } = useGameAudio();

  useEffect(() => {
    if (typeof window !== 'undefined' && !window.flutterExit) {
      window.flutterExit = () => {
        console.log('FlutterExit called - closing webview');
      };
    }
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      handleBackClick();
    };

    if (currentScreen && typeof window !== 'undefined') {
      window.history.replaceState(
        { screen: currentScreen, level: currentLevel },
        '',
        window.location.href
      );
    }

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [currentScreen, currentLevel]);

  const handleBackClick = () => {
    stopAudio();
    setIsPressed(true);
    setTimeout(() => setIsPressed(false), ANIMATIONS.BACK_BUTTON_PRESS_DURATION);

    if (currentScreen === GAME_SCREENS.GAMEPLAY) {
      if (currentLevel > 1) {
        onBack(currentScreen, currentLevel - 1);
        return;
      } else {
        onBack(GAME_SCREENS.LEVEL_SELECTION, currentLevel);
        return;
      }
    } else if (currentScreen === GAME_SCREENS.LEVEL_SELECTION) {
      onBack(GAME_SCREENS.START, 1);
      return;
    } else if (currentScreen === GAME_SCREENS.LEVEL_COMPLETE) {
      onBack(GAME_SCREENS.LEVEL_SELECTION, currentLevel);
      return;
    }

    if (typeof window !== 'undefined' && window.flutterExit) {
      window.flutterExit();
    }
  };

  const positionClasses = {
    'top-left': 'fixed top-4 left-4',
    'top-right': 'fixed top-4 right-16',
    'bottom-left': 'fixed bottom-4 left-4',
    'bottom-right': 'fixed bottom-4 right-4',
    'custom': ''
  };

  const sizeClasses = {
    sm: 'w-10 h-10 text-sm',
    md: 'w-12 h-12 text-base',
    lg: 'w-14 h-14 text-lg'
  };

  const variantClasses = {
    default: 'bg-transparent hover:bg-white/10 shadow-lg',
    minimal: 'bg-transparent hover:bg-white/10',
    floating: 'bg-transparent hover:bg-white/10 shadow-xl'
  };

  const baseClasses = `
    ${positionClasses[position]}
    ${sizeClasses[size]}
    ${variantClasses[variant]}
    z-${Z_INDEX.BACK_BUTTON}
    rounded-lg
    flex items-center justify-center
    transition-all duration-200
    hover:scale-110
    active:scale-95
    ${isPressed ? 'scale-95' : ''}
    ${className}
  `.trim().replace(/\s+/g, ' ');

  return (
    <button
      onClick={handleBackClick}
      className={baseClasses}
      style={style}
      aria-label={BUTTON_TEXT.GO_BACK}
      type="button"
    >
      <img 
        src={ASSET_URLS.BACK_BUTTON}
        alt="Back"
        className="w-full h-full object-contain p-1"
      />
    </button>
  );
};

export type { BackButtonProps };
export { BackButton };
export default BackButton;